/**
 * @file        src/store/booking.store.js
 * @description Zustand store for the booking flow: current hotel/room/rate,
 *              current flight/fare/seat/meal/baggage, auth (loggedIn,
 *              userEmail, userPhone, wallet), and pending-action queue. Mirrors the
 *              top-level `state` object in /Neon Bookign site Design/.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-25 Claude — `fareDeadline`: when the chosen fare's keys stop being
 *                          bookable, as an ABSOLUTE epoch ms and persisted — so a
 *                          refresh cannot hand a customer a fresh window on a fare
 *                          that has two minutes left. Set by setLiveFlight (the
 *                          FARE's own deadline: a one-way books f.priceId, and a
 *                          fare picked from the modal's cabin re-search is younger
 *                          than the card behind it) and by setLiveItinerary (the
 *                          EARLIEST across the legs — a trip dies with its oldest
 *                          key). Cleared wherever the selection is.
 *   - 2026-09-25 Claude — `userPhone`: the signed-in customer's own mobile, the
 *                          twin of `userEmail`, so every checkout's Contact
 *                          details can pre-fill the phone the way it already
 *                          pre-fills the email. It is NOT on the login response
 *                          (`safeUser` carries no mobile), so `refreshContact()`
 *                          reads it from /me/profile — the endpoint that OWNS the
 *                          column the Profile page edits — at sign-in and on
 *                          rehydrate. Best-effort and never throws, exactly like
 *                          refreshWallet(): a checkout that starts with an empty
 *                          phone field is the old behaviour, not a broken one.
 *   - 2026-09-25 Claude — flightSearch.fareTypes: the fare types a search runs under
 *                          (multi-select). `fareType` stays the PRIMARY one, for
 *                          readers that only know one (the fare modal's cabin
 *                          re-search, the group quote). The chosen trip now records
 *                          `fareType` — the type of the fare actually picked — so
 *                          checkout asks for a concession ID only when a Student /
 *                          Senior Citizen fare is what is being booked.
 *   - 2026-09-19 Claude — setLiveFlight() carries the fare's `fareIdentifier` onto the
 *                          trip, as the round-trip / multi-city path (FlightResults
 *                          applyFare) always has. One-way dropped it, so a CORPORATE
 *                          fare booked one-way looked like any other fare to every
 *                          screen downstream — including the one that has to make GST
 *                          mandatory on it.
 *   - 2026-09-16 Claude — `pendingCoupon` (the Exclusive Offer the customer chose to
 *                          book with) is PERSISTED now, and is spent by the first coupon
 *                          surface in its funnel rather than by the payment step. It is
 *                          picked on /offers and only reaches a coupon box several
 *                          screens later, so a refresh in between dropped the offer with
 *                          nothing on screen saying so.
 *   - 2026-09-16 Claude — refreshWallet() now reads `ownerName` off the
 *                          balance view into agencyName. It was
 *                          written ONLY by doLogin(), from `user.agency_name` —
 *                          a field no backend route has ever returned — so the
 *                          Sidebar's "Corporate mode" card and the Wallet page's
 *                          "Shared balance for <company>" line could only render
 *                          their placeholders. The name now rides on /me/wallet,
 *                          which this already calls at sign-in AND on rehydrate,
 *                          so an existing session fills in on its next refresh.
 *   - 2026-09-15 Claude — Leg tabs and add-on rows now carry the backend's
 *                          `segIndex` (the sector's position in the flattened
 *                          itinerary) instead of relying on route alone. Route is
 *                          ambiguous the moment an itinerary repeats one — a
 *                          multi-city DEL-BOM, BOM-DEL, DEL-BOM has two sectors
 *                          answering the same from/to, and `find` took the FIRST,
 *                          so leg 3's options rendered on leg 1's tab and its paid
 *                          seat was attached to leg 1's flight (rejected by the
 *                          supplier AFTER payment). The SERVER's segIndex rides on
 *                          the row, because that is what the book-time re-resolve
 *                          compares against; `segmentId` is regenerated by every
 *                          review and never matched anything.
 *   - 2026-09-11 Claude — setLiveFlight() folds the picked fare into cardForFare(card,
 *                          fare) instead of the bare card (ported from neon). Search
 *                          results now merge every entry of one flight into a single
 *                          card, so a fare can come from an entry with a different
 *                          arrival time; this books it on that entry's own segments,
 *                          which is what reaches the voucher.
 *   - 2026-09-08 Claude — applyReviewedBaggage(): adopt the INCLUDED allowance the
 *                          review reports, per leg group. Everything downstream of
 *                          the fare card (FlightReview's "Check-in baggage" row
 *                          especially) reads the allowance captured at SEARCH, so
 *                          an airline revising it between search and review left
 *                          every screen printing a number the fare no longer
 *                          carried. Called by FlightBook only after the customer
 *                          has accepted the change in BaggageChangedModal.
 *   - 2026-09-08 Claude — A promo code SURVIVED the booking it was used on. Booking
 *                          a flight with a code applied, confirming it, and then
 *                          starting a new booking in the same tab showed that code
 *                          already applied on the new one — the customer never
 *                          chose it, and the discount it displayed was computed
 *                          against a fare it had never been validated for.
 *                          `appliedCoupon` is persisted (sessionStorage) by design
 *                          so a refresh mid-flow keeps it, but nothing ever cleared
 *                          it: not the Confirmation purge (which already deletes
 *                          the identity collected for the booking), not a new
 *                          search, not the sidebar's fresh-start reset. It is now
 *                          dropped at all three, product-scoped via couponReset()
 *                          so a flight search never removes a hotel code or vice
 *                          versa — the single `appliedCoupon` slot is shared.
 *                          Hotels had the identical defect and get the identical
 *                          fix (new HOTEL_SCOPED_KEYS on setHotelSearch, compared
 *                          by value because HotelResults re-sends the same search
 *                          on every mount).
 *   - 2026-09-03 Claude — New `hotelBookingTravellers` field, split out of
 *                          `hotelGuests`. HotelCheckout's proceed() used to
 *                          write its padded traveller list (blank guest slots
 *                          filled with "Guest <lead's surname>" so TripJack
 *                          gets one traveller per priced occupant) straight
 *                          into `hotelGuests` — the SAME field the checkout
 *                          form restores itself from on a step-back. So a
 *                          left-blank Guest 2 came back from Payment showing
 *                          "Guest <Surname>" as if the customer had typed it
 *                          (and that padded copy is what got resaved as the
 *                          draft). `hotelGuests` now stays the as-typed draft
 *                          only; the padded list HotelPayment actually books
 *                          lives in `hotelBookingTravellers`.
 *   - 2026-08-14 Claude — DRIFT REPAIR: a stored seat now carries `ssrCode` (the
 *                          supplier's own seat code) beside `id` (the display label),
 *                          matching neon. Beyond had never carried it, so the book
 *                          payload sent the LABEL as the code and only survived on the
 *                          server's old-client fallback (`r.seatLabel || r.code`) — a
 *                          compatibility branch, not a contract. Falls back to the
 *                          label, so a seat stored before this field existed still books.
 *   - 2026-08-11 Claude — Group booking is now the independent `groupBooking` flag
 *                          (+ setGroupBooking, persisted) instead of a fourth value of
 *                          `tripType`. As a trip type it was mutually exclusive with
 *                          Round trip / Multi-city, so a group could only ever be
 *                          quoted on a single outbound leg — a return or multi-city
 *                          enquiry was unreachable. It is NOT a field of flightSearch:
 *                          setFlightSearch drops the chosen flight whenever a
 *                          fare-scoped field moves, and this toggle re-prices nothing.
 *                          Gained an onRehydrateStorage (there was none) purely to
 *                          migrate sessions persisted as tripType/searchedTripType
 *                          'group' — otherwise they rehydrate a value no radio matches
 *                          and no results branch renders, and the list comes back empty.
 *   - 2026-08-04 Claude — CR B1: added `appliedCoupon` (+ applyCoupon/clearCoupon)
 *                          as a SIBLING of `pendingCoupon`, not a rename/reuse —
 *                          the two are semantically different. `pendingCoupon` is
 *                          a one-shot "carry this code INTO payment and
 *                          auto-apply once" instruction (set by an Exclusive-
 *                          Offer pick, consumed and cleared the moment it fires);
 *                          `appliedCoupon` IS the currently-active coupon
 *                          selection for the in-progress flight booking, set by
 *                          the new step-1 (FlightResults) promo box or by
 *                          FlightPayment's own panel, and it persists
 *                          (sessionStorage) across FlightResults → FlightReview
 *                          → FlightBook → FlightPayment exactly like
 *                          selectedTrip/travellers/seat do, until an explicit
 *                          clearCoupon() or FlightPayment's pre-charge
 *                          re-validation rejects it. Reusing `pendingCoupon` for
 *                          this would have collided its one-shot-and-clear
 *                          semantics with a value that must SURVIVE across steps.
 *   - 2026-08-03 Claude — Added `homeSearchTab` (unpersisted) so the top nav pill and
 *                          the home search card agree on the selected tab.
 *   - 2026-07-28 Claude — setLiveFlight() now FOLDS the picked fare into the card
 *                          (priceId / price / total / supplierPrice / base / taxes /
 *                          name / baggage / seatsLeft), like the round-trip path's
 *                          applyFare has always done. One-way kept the card on its
 *                          CHEAPEST fare while `selectedFare` held the pick, so the
 *                          add-on panels (which read selectedTrip.priceId) offered a
 *                          different fare's baggage/seats than checkout booked
 *                          (selectedFare.priceId): ₹15,000 baggage + ₹1,000 seat were
 *                          quoted on PUBLISHED and dropped at book on LITE, and the
 *                          ₹4,201 gap between the two fares printed as a "Processing
 *                          fee". Live-verified on IX1464: PUBLISHED sells PBAF,
 *                          LITE sells no baggage at all.
 *   - 2026-07-27 Claude — setFlightSearch() now DROPS the chosen flight (selectedTrip
 *                          / selectedFare / selectedLegs + per-flight add-ons) when a
 *                          fare-scoped field changes (pax, route, dates, cabin, fare
 *                          type, multi-city legs). The selection is persisted in
 *                          sessionStorage, so a customer who added a child+infant
 *                          after picking a flight kept the 1-ADULT search's priceIds:
 *                          checkout then quoted one adult's fare for three people and
 *                          TripJack refused the booking with errCode 1000 "Requested
 *                          flight is no longer available". Filters that only narrow
 *                          the list (direct / preferredAirlines / seq) are unaffected.
 *   - 2026-07-20 Claude — Added the mealSel slice (segmentId -> paxIndex -> chosen
 *                          meal) with selectMealFor/clearAllMeals, mirroring
 *                          baggageSel, persisted so it survives the Easebuzz
 *                          redirect.
 *   - 2026-07-16 Claude — Persist `tripType` so a refresh keeps the One-way / Round-trip
 *                          / Multi-city choice — a filled multi-city itinerary no longer
 *                          "vanishes" on the home page (legs were stored but the form
 *                          reverted to one-way and stopped showing them).
 *   - 2026-07-16 Claude — Added persisted `flightPaxDraft` (+ setFlightPaxDraft) so the
 *                          flight traveller form survives a refresh. Cleared by
 *                          freshFlightAddons / resetFlightSearch / the Confirmation purge.
 *   - 2026-06-22 Claude — Initial. Single store for the whole booking UX so
 *                          views don't need to thread props through 5 levels.
 *   - 2026-06-30 Claude — Reset per-flight add-ons (seat / meal / extras /
 *                          travellers) on every setLiveFlight / setLiveItinerary
 *                          so a new flight selection no longer inherits the
 *                          previous flight's seat, meal or passenger names.
 *                          Removed the unused `wheelchair` field.
 *   - 2026-07-06 Claude — resetFlightSearch(): one-way + default flightSearch +
 *                          cleared selections/add-ons, for the sidebar Flights
 *                          entry (fresh search every visit).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { setAccessToken } from '../api/client.js';
import { getWallet } from '../api/wallet.js';
import { getMyProfile } from '../api/hotels.js';
import { cardForFare } from '../api/flights.js';
import { HOTELS } from '../data/hotels.js';
import { ROOMS } from '../data/rooms.js';
import { FLIGHTS } from '../data/flights.js';
import { FARES } from '../data/fares.js';

const initialState = {
  // hotel selection
  hotel: HOTELS[0],
  room: ROOMS[0],
  rate: ROOMS[0].rates[0],
  // flight selection
  flight: FLIGHTS[0],
  fare: FARES[1],
  seat: null,
  seatPrice: 0,
  seatType: '',
  // Per-flight seat selection (round-trip / multi-city have a seat map PER
  // segment). Keyed by segment index; `seat`/`seatPrice`/`seatType` above are
  // kept as aggregates (joined ids / summed price) for the legacy single-seat
  // views (price details, confirmation, voucher).
  seatBySeg: {},
  // Live per-segment, per-passenger excess-baggage picks from TripJack review.
  // Shape: { [segmentId]: { [paxIndex]: { code, amount, weightKg, desc, segmentId } } }.
  // One tier per (segment, passenger); total feeds the price summary + booking.
  baggageSel: {},
  // Live per-segment, per-passenger meal picks from the same TripJack review.
  // Shape: { [segmentId]: { [paxIndex]: { code, amount, desc, segmentId } } }.
  // One meal per (segment, passenger). NOTE amount is often 0 — the airline's
  // special meals (veg, Jain, diabetic, kids) are free but still must be booked.
  mealSel: {},
  // Optional GST details (business input-tax credit) sent to TripJack's book call,
  // and a free-text booking note. null/'' when the traveller doesn't add them.
  gstInfo: null,           // { gstNumber, registeredName, email, mobile, address }
  bookingNotes: '',
  // Chosen TripSafe plan for this flight, or null. Replaces the old `ins`
  // boolean (a flat ₹351 that never reached the backend and bought no policy).
  //   { pid, plid, tier, cover, insurer, premium, nominees: [{ name, relation }] }
  // `premium` here is INDICATIVE — the server re-runs Search+Review at checkout
  // and that price wins. `pid` is the only field the backend trusts.
  insurance: null,
  // Is travel insurance even ON OFFER for the selected flight? Set by the
  // InsuranceAddon from the SUPPLIER's answer: true only when TripSafe returns at
  // least one plan for this itinerary. Domestic trips (TripSafe is
  // international-only), unsupported routes and out-of-range traveller ages all
  // come back with nothing — and then no other page may offer or mention
  // insurance, or we invite the customer to add a product that does not exist.
  insuranceAvailable: false,
  paxName: '',
  mode: 'hotel',           // 'hotel' | 'flight'
  // Which search tab the home card is showing ('flights' | 'hotels' | 'insurance').
  // Shared state, NOT a Home-local useState: the top nav has to highlight the same
  // tab, and it cannot read it from location.state — Home consumes and clears that
  // on arrival (so a refresh starts blank), which snapped the nav pill back to
  // Flights. Deliberately NOT persisted, so F5 still lands on Flights.
  homeSearchTab: 'flights',
  // search context
  tripType: 'oneway',      // 'oneway' | 'return' | 'multicity'
  // Group booking is a MODE, not a trip type. It used to be a fourth radio
  // alongside One-way / Round trip / Multi-city, which made "a group booking"
  // and "a round trip" mutually exclusive — there was no way to ask for a quote
  // on anything but a single outbound leg. It is now an independent flag that
  // rides on top of whichever trip type is selected: when it is on, the results
  // page offers "GET QUOTE" (the existing group-quote form) in place of the
  // button that walks into checkout. It is NOT part of flightSearch — it does
  // not change the supplier query, only what the CTA does.
  groupBooking: false,
  nights: 1,
  dates: 'Fri, Jun 12 → Sat, Jun 13',
  // ---- live hotel booking context (v3 flow) ----
  // hotelSearch drives /hotel-searchlist + /hotelDetail-search.
  hotelSearch: {
    city: '',              // city_code from /hotel-search autocomplete (city pick)
    hids: [],              // explicit hotel ids (hotel pick) — alternative to city
    cityLabel: '',         // human label shown in the search bar
    checkIn: '',           // YYYY-MM-DD
    checkOut: '',          // YYYY-MM-DD
    rooms: [{ numberOfAdults: 2, numberOfChild: 0 }],
  },
  // selectedOption carries the chosen room option through review → book.
  selectedOption: null,    // { hotelId, optionId, price, roomName, reviewHash, correlationId }
  hotelBookingId: null,    // returned by /hotel-review, consumed by /hotel-book
  hotelBookingRef: null,   // returned by /hotel-book (supplier confirmation id)
  guest: null,             // lead guest { firstName, lastName, email, phone, pan }
  // Which booking `guest`/`hotelGuests` were typed for: `hotelId|optionId|checkIn`.
  // Checkout only re-uses saved details when this still matches the current
  // selection, so identity (name, email, phone, PAN) can never pre-fill somebody
  // else's booking — while a refresh or a step back still restores your own form.
  guestFor: null,
  // The checkout form AS TYPED — one entry per guest the customer has actually
  // added, blank fields left blank. Restores the editable form on a step-back
  // or refresh. NEVER padded — see hotelBookingTravellers for the list that
  // is actually booked.
  hotelGuests: [],
  // The real traveller list sent to TripJack at booking time: one entry per
  // PRICED occupant, with any slot the customer left blank padded to
  // "Guest <lead's surname>" (buildTravellers() in HotelCheckout) so the
  // count always matches what was priced. Kept SEPARATE from hotelGuests —
  // writing this padded list back into hotelGuests used to leak the
  // synthetic "Guest <Surname>" into the checkout form itself on a step-back
  // (and onto the actual ticket, since that padded copy is what got resaved
  // as the draft). [{ room, type:'ADULT'|'CHILD', firstName, lastName, age,
  // isLead, pan? }] — the backend maps this to TripJack's
  // roomTravellerInfo[].travellerInfo[].
  hotelBookingTravellers: [],
  // Optional free-text special request for the hotel, captured at checkout and
  // sent to TripJack in the book call (per-room ssr) — a REAL supplier request,
  // not a post-booking note. Empty string = none (the book payload omits it).
  hotelSpecialRequest: '',
  // ---- live flight booking context (v1 air flow) ----
  // flightSearch drives /air-search.
  flightSearch: {
    from: 'BOM',           // origin airport IATA code
    to: 'DEL',             // destination airport IATA code
    fromLabel: 'Mumbai (BOM)',
    toLabel: 'New Delhi (DEL)',
    departDate: '',        // YYYY-MM-DD
    returnDate: '',        // YYYY-MM-DD (return trip — wired later)
    cabinClass: 'ECONOMY', // ECONOMY | PREMIUM_ECONOMY | BUSINESS | FIRST
    adults: 1,
    children: 0,
    infants: 0,
    direct: false,            // direct flights only
    fareType: 'REGULAR',      // PRIMARY fare type (first of fareTypes)
    fareTypes: ['REGULAR'],   // every ticked fare type, in FARE_TYPE_ORDER
    preferredAirlines: [],     // [] = any airline, else array of IATA codes
    // multi-city legs (only used when tripType === 'multicity'). Each leg is an
    // independent one-way: { from, to, date, fromLabel, toLabel }. Edited in the
    // search form; FlightResults searches the active leg live.
    legs: [
      { from: 'DEL', to: 'BOM', date: '', fromLabel: 'New Delhi (DEL)', toLabel: 'Mumbai (BOM)' },
      { from: 'BOM', to: 'BLR', date: '', fromLabel: 'Mumbai (BOM)', toLabel: 'Bengaluru (BLR)' },
    ],
  },
  // selectedTrip is the chosen live flight card (adapted from air-search). It
  // carries priceId / supplierPrice / taxes the checkout + payment need.
  selectedTrip: null,
  selectedFare: null,      // { priceId, name, price, total, supplierPrice, base, taxes }
  selectedLegs: [],        // round-trip / multi-city: the chosen card per leg
  // When the chosen fare's keys stop being bookable — epoch ms, the EARLIEST
  // across the selected legs (see earliestDeadline). An absolute instant, not a
  // remaining duration, so a page refresh cannot restart the countdown on a fare
  // that is already half dead. null = no deadline known (an older backend, or a
  // session persisted before this shipped): callers must read that as "no
  // countdown", never as "expired".
  fareDeadline: null,
  travellers: [],          // [{ title, firstName, lastName, dob, type }]
  // Live traveller-form draft (names, DOB, passport, FF) mirrored from the Flight
  // Book page so a page refresh restores what the customer typed instead of wiping
  // the form. Cleared on a new flight selection (freshFlightAddons), a new search
  // (resetFlightSearch) and after a booking completes (the Confirmation purge).
  flightPaxDraft: [],
  flightBookingRef: null,  // PNR / supplier ref (set on confirmation redirect)
  // auth
  loggedIn: false,
  userEmail: '',
  // The customer's own mobile, from their profile (`users.mobile`, stored as
  // "<dial code> <digits>" — the shape PhoneInput parses). Every checkout's
  // Contact details pre-fills it; the customer can still type over it, and
  // what they type is the booking's contact number, not a profile edit.
  // Empty for a guest, and for a session whose /me/profile read failed.
  userPhone: '',
  // B2B agent identity. `role` is 'b2c_user' | 'b2b_owner' | 'b2b_user' | 'guest';
  // agents carry a businessId (their agency) which drives agent pricing/commission.
  role: '',
  businessId: null,
  agencyName: '',
  agentCode: '',
  walletBalance: 0,
  // Wallet account status ('active' | 'frozen' | 'closed'). A frozen wallet
  // hides the "use wallet" option and the server rejects any spend.
  walletStatus: 'active',
  // WALLET DUES (2026-10-06). `walletDues` is what is OWED — granted money that
  // has been spent — and is never negative. `walletNet` is balance - dues, the
  // headline the customer sees, and IS allowed to be negative. Kept apart on
  // purpose: netting them into one figure loses which half is which, and the
  // recharge screen has to say how much of a top-up clears the debt before any
  // of it becomes spendable.
  walletDues: 0,
  walletNet: 0,
  walletDuesSince: null,
  // Spendable money that is REPAYABLE — spending it creates a due. Owned money
  // is always allocated first, so this is what the checkout uses to say how much
  // of a wallet payment will have to be paid back.
  walletRepayable: 0,
  // The TENANT-WIDE wallet kill-switch (admin console -> Wallet Management ->
  // Wallet Settings -> "Wallet enabled"), mirrored from GET /me/wallet.
  // Distinct from walletStatus: that is ONE customer being frozen, this is the
  // whole feature being off for everybody. Frozen still SHOWS the wallet (with
  // the option disabled, so the customer can see their money is there);
  // switched off hides every wallet surface outright. Defaults to true, the
  // same default the server applies when the settings row is missing, so the
  // wallet never blinks out while the first refreshWallet() is in flight.
  walletFeatureEnabled: true,
  // Wallet amount applied to the in-flight payment; the SERVER debits (captures)
  // it on booking success and releases it on failure — the confirmation page just
  // re-fetches the real balance, it no longer guesses locally.
  pendingWalletDebit: 0,
  pendingAction: null,
  // An "Exclusive Offer" coupon the customer chose to book with: { code, product }
  // ('flight' | 'hotel'). A one-shot instruction, not a selection: the FIRST
  // coupon surface in the matching funnel (<PromoCodeBox> on FlightBook /
  // FlightReview / HotelCheckout, else the payment panel) validates it, writes
  // the result to `appliedCoupon` and clears this slot — see hooks/usePendingCoupon.
  // Persisted (sessionStorage) like `appliedCoupon`: the customer picks the offer
  // on /offers and only reaches a coupon surface several screens later, so a
  // refresh anywhere in between used to drop the offer with no sign it was gone.
  // It cannot re-fire after it lands (it clears itself), so persisting it cannot
  // resurrect a code the customer removed.
  pendingCoupon: null,
  // CR B1 — the coupon CURRENTLY applied to the in-progress flight booking, set
  // by the step-1 (FlightResults) promo box or FlightPayment's own promo panel:
  // { code, discountType, discountValue, product }. Unlike `pendingCoupon` (a
  // one-shot "carry this INTO payment and auto-apply once" instruction, cleared
  // the moment it fires), `appliedCoupon` IS the active selection — it persists
  // across FlightResults → FlightReview → FlightBook → FlightPayment so the
  // discount shows consistently at every step, and is only removed by an
  // explicit clearCoupon() (customer clicks "remove") or by FlightPayment's
  // pre-charge re-validation rejecting it (fare/itinerary changed since step 1,
  // or the code was disabled/expired/exhausted in between) — never silently.
  // Persisted (sessionStorage) like every other live-booking field below, so a
  // refresh mid-flow doesn't drop it.
  appliedCoupon: null,
  // ui
  navCollapsed: false,
  navOpen: false,
};

// The fare deadline of a whole trip: the EARLIEST of its legs'. A round trip
// whose two legs came from searches a few minutes apart is only bookable for as
// long as its OLDEST key survives, so the trip dies with that leg — taking the
// latest would show time the booking does not have. Legs with no stamp are
// skipped rather than counted as zero; no stamped leg at all means no deadline.
const earliestDeadline = (cards) => {
  const ds = (cards || []).map((c) => Number(c?.expiresAt) || 0).filter((n) => n > 0);
  return ds.length ? Math.min(...ds) : null;
};

// Per-flight booking add-ons (seat, meal, extras, travellers). These are
// specific to ONE chosen flight, but they live in the persisted store, so a
// fresh flight/itinerary selection must reset them — otherwise the previous
// flight's seat / meal / passenger names carry over to the next booking.
const freshFlightAddons = () => ({
  seat: null,
  seatPrice: 0,
  seatType: '',
  seatBySeg: {},
  baggageSel: {},
  mealSel: {},
  gstInfo: null,
  bookingNotes: '',
  insurance: null,
  insuranceAvailable: false,
  travellers: [],
  flightPaxDraft: [],
  guest: null,
  paxName: '',
});

// The flightSearch fields a supplier fare is quoted AGAINST. If any of them
// changes, every priceId already in the store belongs to a different trip and
// must not be carried into checkout (see setFlightSearch). Filters that only
// narrow the RESULT LIST (direct, preferredAirlines) are deliberately absent —
// they don't re-price a fare the customer already picked.
const FARE_SCOPED_KEYS = [
  'adults', 'children', 'infants',
  'from', 'to', 'departDate', 'returnDate',
  'cabinClass', 'fareType', 'fareTypes', 'legs',
];
// Multi-city `legs` are compared on the three fields that price a fare (route +
// date) — never on the whole object, whose labels are re-derived on every mount
// of the search form and would otherwise read as a change and wipe a live
// selection. Everything else is a scalar.
const legScope = (v) => (Array.isArray(v) ? v : [])
  .map((l) => `${l?.from || ''}>${l?.to || ''}@${l?.date || ''}`)
  .join('|');
const sameScopeValue = (key, a, b) =>
  (key === 'legs' ? legScope(a) === legScope(b) : String(a ?? '') === String(b ?? ''));

// The hotelSearch fields a stay is quoted AGAINST — the hotel-side twin of
// FARE_SCOPED_KEYS. A coupon is validated for ONE booking, so a change to any of
// these starts a different one and must not carry the previous code.
// Compared by VALUE, never by object identity: HotelResults re-resolves the same
// search out of the URL on every mount and re-sends it, and that is not a change.
const HOTEL_SCOPED_KEYS = ['city', 'hids', 'checkIn', 'checkOut', 'rooms'];
const hotelScope = (key, v) => {
  if (key === 'rooms') {
    return (Array.isArray(v) ? v : [])
      .map((r) => `${r?.numberOfAdults || 0}a${r?.numberOfChild || 0}c`).join('|');
  }
  if (key === 'hids') return (Array.isArray(v) ? v : []).join(',');
  return String(v ?? '');
};
const sameHotelScopeValue = (key, a, b) => hotelScope(key, a) === hotelScope(key, b);

// Drop the applied coupon — but ONLY when it belongs to the product whose search
// just changed. `appliedCoupon` is a single slot shared by flights and hotels
// (scoped by its own `product` field), so an unscoped reset here would let a new
// flight search silently remove a hotel code the customer applied in the same tab.
const couponReset = (s, product) =>
  (s.appliedCoupon && s.appliedCoupon.product !== product ? {} : { appliedCoupon: null });

export const useBooking = create(persist((set, get) => ({
  ...initialState,

  set: (patch) => set(patch),
  reset: () => set(initialState),

  // ---- auth flow (demo / in-memory OTP — backend wires in later) ----
  /**
   * @param {string} email
   * @param {object} [user] the backend's safeUser — carries role/business_id so a
   *                        B2B agent is recognised after sign-in (B2C users have none).
   */
  doLogin(email, user) {
    const e = email || get().userEmail || 'guest@beyonddestination.com';
    set({
      loggedIn: true,
      userEmail: e,
      // Cleared, not carried: signing in as somebody else must never leave
      // the previous account's number in the next checkout's phone field.
      userPhone: '',
      // Real DB-backed balance from the login response (replaces the old
      // hardcoded ₹1250). refreshWallet() below also pulls the live status.
      walletBalance: Number(user?.wallet_balance) || 0,
      role: user?.role || 'b2c_user',
      businessId: user?.business_id ?? null,
      agencyName: user?.agency_name || '',
      agentCode: user?.agent_code || '',
    });
    // Fetch the authoritative balance + status (fire-and-forget).
    get().refreshWallet();
    get().refreshContact();
    const pending = get().pendingAction;
    if (typeof pending === 'function') {
      set({ pendingAction: null });
      setTimeout(pending, 0);
    }
  },

  /**
   * Pull the live wallet balance + status from the server. The server owns the
   * balance now, so this is the single source of truth the sidebar / wallet page
   * / checkout read after any money movement. Best-effort — never throws.
   */
  async refreshWallet() {
    if (!get().loggedIn) return;
    try {
      const v = await getWallet();
      set({
        walletBalance: Number(v?.balance) || 0,
        walletStatus: v?.status || 'active',
        walletDues: Number(v?.dues) || 0,
        walletNet: v?.net === undefined ? (Number(v?.balance) || 0) : Number(v.net),
        walletDuesSince: v?.duesSince || null,
        walletRepayable: Number(v?.repayableAvailable) || 0,
        // The tenant-wide kill-switch. `enabled` is absent only on a backend
        // older than this field, so treat undefined as ON rather than hiding
        // the wallet against an older server.
        walletFeatureEnabled: v?.enabled === undefined ? true : !!v.enabled,
        // The COMPANY this pool belongs to. Only a business pool answers with a
        // name, and this is the only route that carries it — so a session that
        // was already signed in when this shipped picks it up on its next
        // refresh (this runs from onRehydrateStorage too), and a company renamed
        // in the admin console follows on the next page load. Conditional: a
        // retail wallet sends null and must not wipe what doLogin set.
        ...(v?.ownerName ? { agencyName: v.ownerName } : {}),
      });
    } catch (_) { /* keep the last known values */ }
  },

  /**
   * Pull the customer's own mobile from /me/profile into `userPhone`.
   *
   * The login response carries no mobile (`safeUser` is id/email/name/role/
   * wallet), and /me/profile is the endpoint that owns the column the Profile
   * page edits — so it is the one source for this, rather than a second copy
   * riding on the auth payload that could drift from it. Runs at sign-in and on
   * rehydrate (a session persisted before this shipped has no phone yet).
   * Best-effort — never throws: an empty field is the old behaviour.
   */
  async refreshContact() {
    if (!get().loggedIn) return;
    try {
      const p = await getMyProfile();
      const mobile = String(p?.mobile || '').trim();
      if (mobile) set({ userPhone: mobile });
    } catch (_) { /* leave the field empty; the customer types it */ }
  },
  logout() {
    setAccessToken(null); // clear the persisted JWT so authed calls stop working
    set({
      loggedIn: false, userEmail: '', userPhone: '', walletBalance: 0, walletStatus: 'active',
      walletDues: 0, walletNet: 0, walletDuesSince: null, walletRepayable: 0,
      walletFeatureEnabled: true, pendingAction: null,
      role: '', businessId: null, agencyName: '', agentCode: '',
    });
  },
  requireLogin(action) {
    if (get().loggedIn) { action?.(); return true; }
    // Not logged in: stash the intent and open the auth modal. On successful
    // login, doLogin() runs the stashed action (e.g. navigate to flight book).
    // Without this dispatch, "Book Now"/"Continue" silently did nothing.
    set({ pendingAction: action });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('nh:open-auth'));
    }
    return false;
  },

  // ---- nav ----
  toggleNavCollapsed: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
  openNav: () => set({ navOpen: true }),
  closeNav: () => set({ navOpen: false }),

  // Fresh flight start (sidebar "Flights"): one-way defaults, previous search
  // (route/dates/pax/airlines/fare type) wiped, previous trip selections and
  // per-flight add-ons cleared. Deep-copied so edits never mutate initialState.
  resetFlightSearch() {
    set((s) => ({
      tripType: 'oneway',
      groupBooking: false,
      flightSearch: JSON.parse(JSON.stringify(initialState.flightSearch)),
      selectedTrip: null,
      selectedFare: null,
      selectedLegs: [],
      fareDeadline: null,
      travellers: [],
      ...couponReset(s, 'flight'),
      ...freshFlightAddons(),
    }));
  },

  // Adopt the allowance the REVIEW says the fare actually includes, keyed by
  // "FROM-TO" of each leg group. The cards, `flight` and `selectedTrip` all carry
  // the allowance the SEARCH advertised, and FlightReview prints it verbatim
  // (`fl.baggage.checkIn`) — so when the airline revises it between search and
  // review, every screen after the fare card kept showing a number the fare no
  // longer carried. Called only once the customer has SEEN the change and
  // accepted it (BaggageChangedModal): this corrects the display, it never
  // silently rewrites what they were shown.
  applyReviewedBaggage(byRoute) {
    if (!byRoute || !Object.keys(byRoute).length) return;
    set((s) => {
      const patch = (leg) => {
        const next = leg && byRoute[`${leg.fromCode}-${leg.toCode}`];
        return next ? { ...leg, baggage: { ...(leg.baggage || {}), ...next } } : leg;
      };
      return {
        selectedLegs: (s.selectedLegs || []).map(patch),
        flight: patch(s.flight),
        selectedTrip: patch(s.selectedTrip),
      };
    });
  },

  // Group-booking toggle. Deliberately its OWN setter rather than a field of
  // setFlightSearch: that reducer DROPS the chosen flight whenever a fare-scoped
  // field moves, and flipping this checkbox re-prices nothing — the search, the
  // filters and the current selection must all survive it untouched.
  setGroupBooking: (on) => set({ groupBooking: !!on }),

  // ---- selection helpers ----
  selectHotel(id) {
    const h = HOTELS.find((x) => x.id === id) || HOTELS[0];
    set({ hotel: h, room: ROOMS[0], rate: ROOMS[0].rates[0] });
  },
  selectRoom(i) {
    const room = ROOMS[i] || ROOMS[0];
    set({ room, rate: room.rates[0] });
  },
  selectRate(rateIndex) {
    const room = get().room;
    set({ rate: room.rates[rateIndex] });
  },
  selectFlight(id) {
    const f = FLIGHTS.find((x) => x.id === id) || FLIGHTS[0];
    set({ flight: f });
  },
  selectFare(i) {
    set({ fare: FARES[i] || FARES[0] });
  },
  selectSeat({ id, price, type }) {
    set({ seat: id, seatPrice: price, seatType: type || '' });
  },
  // Pick (or clear, when id is null) the seat for ONE passenger on one flight
  // segment, then recompute the aggregate seat/seatPrice/seatType across every
  // segment × passenger. `seatBySeg` is nested: { [segKey]: { [paxIndex]: seat } }
  // so each traveller keeps their own seat per leg (2 adults + 1 child = up to 3
  // seats per leg). `segId` is the TripJack seat-map segment id — kept so the book
  // call can attach the seat SSR with the right key + paxIndex.
  selectSeatFor(key, paxIndex, { id, ssrCode, price, type, segId, segIndex, from, to } = {}) {
    set((s) => {
      const seats = { ...(s.seatBySeg || {}) };
      const seg = { ...(seats[key] || {}) };
      // ssrCode = supplier seat code for the Book SSR (id is the display label).
      // Defaulted to the label so a seat stored before this field existed — or one
      // on a supplier that gives no separate code — still books.
      if (id) seg[paxIndex] = { id, ssrCode: ssrCode || id, price: price || 0, type: type || '', segId: segId || null, segIndex: segIndex ?? null, from: from || '', to: to || '' };
      else delete seg[paxIndex];
      if (Object.keys(seg).length) seats[key] = seg;
      else delete seats[key];
      const entries = [];
      for (const sg of Object.values(seats)) for (const e of Object.values(sg || {})) if (e) entries.push(e);
      return {
        seatBySeg: seats,
        seat: entries.map((e) => e.id).join(', ') || null,
        seatPrice: entries.reduce((a, e) => a + (e.price || 0), 0),
        seatType: entries[0]?.type || '',
      };
    });
  },
  clearAllSeats() {
    set({ seatBySeg: {}, seat: null, seatPrice: 0, seatType: '' });
  },

  // Select (or clear, when option is null) one excess-baggage tier for a given
  // segment + passenger. One tier per (segment, passenger): re-selecting the
  // same tier clears it (toggle); picking another replaces it.
  selectBaggageFor(segmentId, paxIdx, option) {
    set((s) => {
      const all = { ...(s.baggageSel || {}) };
      const seg = { ...(all[segmentId] || {}) };
      const cur = seg[paxIdx];
      if (!option || (cur && cur.code === option.code)) delete seg[paxIdx];
      else seg[paxIdx] = { ...option, segmentId };
      if (Object.keys(seg).length) all[segmentId] = seg;
      else delete all[segmentId];
      return { baggageSel: all };
    });
  },
  clearAllBaggage() {
    set({ baggageSel: {} });
  },

  // Select (or clear, when option is null) one meal for a given segment +
  // passenger. Same toggle semantics as baggage: re-picking the chosen meal
  // clears it, picking another replaces it.
  selectMealFor(segmentId, paxIdx, option) {
    set((s) => {
      const all = { ...(s.mealSel || {}) };
      const seg = { ...(all[segmentId] || {}) };
      const cur = seg[paxIdx];
      if (!option || (cur && cur.code === option.code)) delete seg[paxIdx];
      else seg[paxIdx] = { ...option, segmentId };
      if (Object.keys(seg).length) all[segmentId] = seg;
      else delete all[segmentId];
      return { mealSel: all };
    });
  },
  clearAllMeals() {
    set({ mealSel: {} });
  },

  // ---- live hotel helpers (v3 flow) ----
  setHotelSearch(patch) {
    set((s) => {
      const next = { ...s.hotelSearch, ...patch };
      // Same rule as setFlightSearch: a coupon belongs to the booking it was
      // applied to. Without this, a code used on one stay reappeared on the next
      // search in the same tab, already applied and never chosen.
      const changed = HOTEL_SCOPED_KEYS.some(
        (k) => k in (patch || {}) && !sameHotelScopeValue(k, s.hotelSearch[k], next[k]),
      );
      if (!changed) return { hotelSearch: next };
      return { hotelSearch: next, ...couponReset(s, 'hotel') };
    });
  },
  // Set the active hotel from a live (adapted) card object — bypasses the
  // static HOTELS lookup used by selectHotel(id).
  setHotel(card) {
    set({ hotel: card });
  },
  // Persist the chosen room option so checkout/review/book can chain it.
  selectOption(option) {
    set({ selectedOption: option });
  },

  // ---- live flight helpers (v1 air flow) ----
  // A search change that re-prices the trip INVALIDATES the chosen flight: the
  // supplier's priceIds were quoted for the old passengers/route/dates/cabin, and
  // the selection lives in sessionStorage, so it used to survive a new search.
  // That is how a fare searched for 1 ADULT reached checkout while the traveller
  // form (which reads the LIVE pax counts) collected 1 adult + 1 child + 1 infant:
  // the review priced the old mix, the quote showed a single adult's fare for
  // three people, and TripJack refused the booking with errCode 1000 "Requested
  // flight is no longer available" — after the customer had filled everything in.
  // Changing any fare-scoped field now drops the selection (and its add-ons), so
  // the customer re-picks a flight priced for the trip they actually asked for.
  // `seq`-only patches (a plain re-search / chip removal) change nothing here.
  setFlightSearch(patch) {
    set((s) => {
      const next = { ...s.flightSearch, ...patch };
      const changed = FARE_SCOPED_KEYS.some(
        (k) => k in (patch || {}) && !sameScopeValue(k, s.flightSearch[k], next[k]),
      );
      if (!changed) return { flightSearch: next };
      return {
        flightSearch: next,
        selectedTrip: null,
        selectedFare: null,
        selectedLegs: [],
        fareDeadline: null,
        // A promo code applied to the previous trip is not a promo code the
        // customer chose for THIS one — see couponReset.
        ...couponReset(s, 'flight'),
        ...freshFlightAddons(),
      };
    });
  },
  // Set the active flight from a live (adapted) card object + its chosen fare.
  // Mirrors selectFlight() but bypasses the static FLIGHTS lookup and records
  // the live booking keys. Also writes `flight`/`fare` so the existing add-on /
  // confirmation views (which read those) keep working unchanged.
  setLiveFlight(card, fare) {
    const f = fare || (card?.fares && card.fares[0]) || null;
    // The card as adapted from the search describes its CHEAPEST fare option
    // (price / base / taxes / supplierPrice / priceId / baggage all come from
    // `best`). When the customer picks a DIFFERENT fare in the modal, the card
    // has to become that fare — otherwise the two halves of checkout disagree:
    // the add-on panels query `selectedTrip.priceId` (the card) while payment
    // books `selectedFare.priceId` (the pick). That is exactly how a booking
    // priced on PUBLISHED (₹999 base, sells PBAF ₹15,000 baggage) got ticketed
    // on LITE (₹5,000 base, sells NO baggage): ₹15,000 of baggage and a ₹1,000
    // seat were offered, quoted, then silently dropped at book, and the ₹4,201
    // gap between the two fares surfaced on screen as a "Processing fee".
    // Same projection the round-trip/multi-city path has always used
    // (FlightResults applyFare), plus the per-fare baggage allowance + seat count
    // so the displayed inclusions describe the fare being sold too.
    //
    // cardForFare, not the card itself: since one card now holds every entry of
    // a flight, the picked fare may come from an entry with its OWN schedule
    // (same AI2430 10:00, a 12:20 arrival instead of 12:15). Booking persists
    // this object's `_raw.sI` into the voucher, so it must be that fare's trip.
    const trip = f?.priceId
      ? {
        ...cardForFare(card, f),
        priceId: f.priceId,
        price: f.price,
        total: f.total,
        supplierPrice: f.supplierPrice,
        baseFare: f.base,
        taxes: f.taxes,
        fareName: f.name,
        // Which fare FAMILY the trip is on (CORPORATE, SPECIAL_RETURN, …). The
        // round-trip / multi-city path (FlightResults applyFare) has always
        // carried it; one-way dropped it, so a Corporate fare booked one-way
        // looked like any other fare to every screen downstream — including the
        // one that has to make GST mandatory on it.
        fareIdentifier: f.fareIdentifier || '',
        // The fare type this fare was SEARCHED under (see searchFlightsByFareTypes)
        // — what decides whether checkout needs a concession ID.
        fareType: f.pft || card?.fareType || '',
        baggage: f.baggage || card?.baggage || null,
        seatsLeft: f.seats > 0 ? f.seats : null,
      }
      : card;
    set({
      // A new flight is a new booking — clear the previous flight's seat / meal /
      // extras / passenger names so they don't leak across selections.
      ...freshFlightAddons(),
      mode: 'flight',
      // A one-way selection must clear any round-trip/multi-city legs left in the
      // store (they persist in sessionStorage), otherwise the payment step would
      // send their stale priceIds and TripJack rejects the review with
      // "Number of PriceId ... doesn't match with the requested trips".
      selectedLegs: [],
      selectedTrip: trip,
      selectedFare: f,
      // The FARE's own deadline, not the earliest of what is lying around: a
      // one-way books f.priceId, and a fare picked from the modal's cabin
      // re-search is YOUNGER than the card behind it. Taking a minimum here
      // would expire the booking on a key it is not using.
      fareDeadline: earliestDeadline([f]) || earliestDeadline([trip, card]),
      flight: trip,
      fare: f ? { name: f.name, price: f.total ?? f.price, cabin: card?.cabinClass } : null,
    });
  },
  // CR B1 — set/clear the coupon applied to the in-progress flight booking. `row`
  // is either a raw `coupon_codes` row (discount_type/discount_value) or an
  // already-normalised { discountType, discountValue } — both shapes are
  // accepted so the same action serves the offers-list pick, the typed-code
  // validation response, and the pendingCoupon auto-apply.
  applyCoupon(row, product = 'flight') {
    if (!row?.code) return;
    set({
      appliedCoupon: {
        code: row.code,
        discountType: row.discountType ?? row.discount_type,
        discountValue: Number(row.discountValue ?? row.discount_value) || 0,
        product,
      },
    });
  },
  clearCoupon() {
    set({ appliedCoupon: null });
  },
  setTravellers(list) {
    set({ travellers: Array.isArray(list) ? list : [] });
  },
  // Mirror the live traveller-form draft so a refresh restores it. Kept separate
  // from `travellers` (the submitted booking payload) so the two never collide.
  setFlightPaxDraft(list) {
    set({ flightPaxDraft: Array.isArray(list) ? list : [] });
  },
  // Round-trip / multi-city: `legs` are the chosen cards (one per leg). Sums the
  // per-leg totals and collects every priceId so checkout reviews+books them
  // together. `flight`/`fare`/`selectedTrip` use the first leg for display +
  // backward-compat with the single-leg views; selectedTrip carries priceIds[].
  setLiveItinerary({ legs, label }) {
    const valid = (legs || []).filter(Boolean);
    if (!valid.length) return;
    const sum = (k) => valid.reduce((s, c) => s + (Number(c[k]) || 0), 0);
    const priceIds = valid.map((c) => c.priceId).filter(Boolean);
    const total = sum('total');
    const primary = valid[0];
    set({
      // New itinerary → fresh add-ons (see setLiveFlight).
      ...freshFlightAddons(),
      mode: 'flight',
      selectedLegs: valid,
      selectedTrip: {
        ...primary,
        priceIds,
        total,
        supplierPrice: sum('supplierPrice'),
        baseFare: sum('baseFare'),
        taxes: sum('taxes'),
        // A concession on ANY leg makes the booking a concession booking: the ID
        // card is collected once per passenger, for the whole trip.
        fareType: (valid.find((c) => c.fareType && c.fareType !== 'REGULAR') || primary).fareType || '',
      },
      selectedFare: { name: label || 'Round trip', price: total, total },
      fareDeadline: earliestDeadline(valid),
      flight: primary,
      fare: { name: label || primary.fareName || 'Published', price: total },
    });
  },
}), {
  // Persist the hotel booking-flow context to sessionStorage so a page refresh
  // (e.g. on /hotels/results or /hotels/detail) keeps the search + selection and
  // the live queries re-run instead of falling back to "start a search". Auth /
  // UI / pendingAction are intentionally left out.
  name: 'nh-booking',
  storage: createJSONStorage(() => sessionStorage),
  partialize: (s) => ({
    // Auth — persist the sign-in so a page refresh keeps the session. Only the
    // explicit logout() clears it (pendingAction is a function, never persisted).
    loggedIn: s.loggedIn,
    userEmail: s.userEmail,
    userPhone: s.userPhone,
    walletBalance: s.walletBalance,
    walletStatus: s.walletStatus,
    walletDues: s.walletDues,
    walletNet: s.walletNet,
    walletDuesSince: s.walletDuesSince,
    walletRepayable: s.walletRepayable,
    walletFeatureEnabled: s.walletFeatureEnabled,
    // B2B identity — without these a hard refresh rehydrates `loggedIn` +
    // `walletBalance` but drops `role` back to '', so isCompanyWallet reads
    // false and a corporate account's wallet/sidebar silently flip to the
    // personal view until the next explicit login.
    role: s.role,
    businessId: s.businessId,
    agencyName: s.agencyName,
    agentCode: s.agentCode,
    pendingWalletDebit: s.pendingWalletDebit,
    hotelSearch: s.hotelSearch,
    hotel: s.hotel,
    room: s.room,
    rate: s.rate,
    selectedOption: s.selectedOption,
    hotelBookingId: s.hotelBookingId,
    hotelBookingRef: s.hotelBookingRef,
    guest: s.guest,
    guestFor: s.guestFor,
    hotelGuests: s.hotelGuests,
    hotelBookingTravellers: s.hotelBookingTravellers,
    nights: s.nights,
    dates: s.dates,
    mode: s.mode,
    // live flight context — keeps search + selection across a page refresh on
    // /flights/results|book|review|payment so the queries/selection survive.
    // tripType is persisted too, or a refresh would drop a One-way / Round-trip /
    // Multi-city choice back to the 'oneway' default — which made a filled
    // multi-city itinerary "vanish" on the home page (its legs were still stored,
    // but the form no longer showed the multi-city rows).
    tripType: s.tripType,
    // Persisted for the same reason tripType is: a refresh on the results page
    // must not silently turn a group enquiry back into a normal booking.
    groupBooking: s.groupBooking,
    flightSearch: s.flightSearch,
    selectedTrip: s.selectedTrip,
    selectedFare: s.selectedFare,
    selectedLegs: s.selectedLegs,
    // Persisted BECAUSE it is absolute: rehydrating it is what stops a refresh
    // from handing a customer a fresh 15 minutes on a fare that has 2 left.
    fareDeadline: s.fareDeadline,
    travellers: s.travellers,
    flightPaxDraft: s.flightPaxDraft,
    flight: s.flight,
    fare: s.fare,
    // Chosen seat + add-ons must persist too, or the Easebuzz redirect (which
    // leaves and re-enters the SPA) loses them and the confirmation falls back to
    // "Assigned at check-in" even though the customer picked them.
    seat: s.seat,
    seatPrice: s.seatPrice,
    seatType: s.seatType,
    seatBySeg: s.seatBySeg,
    baggageSel: s.baggageSel,
    mealSel: s.mealSel,
    // Must survive the Easebuzz redirect — the payment page reads the premium.
    insurance: s.insurance,
    // Persisted too: /flights/review renders the add-on row from this, and a
    // refresh there must not make an offered product silently vanish.
    insuranceAvailable: s.insuranceAvailable,
    paxName: s.paxName,
    // CR B1 — the coupon applied at step 1 (or at payment) must survive a
    // refresh on ANY step, same as the flight selection it discounts.
    appliedCoupon: s.appliedCoupon,
    // …and so must the not-yet-applied Exclusive Offer the customer walked in
    // with, which has to survive the whole search before it reaches a coupon box.
    pendingCoupon: s.pendingCoupon,
  }),
  // 'group' used to be a fourth TRIP TYPE; it is now the independent
  // `groupBooking` flag. A session persisted before that change rehydrates a
  // tripType no radio matches and no results branch renders, so the flight list
  // would come back empty until the customer re-searched. Translate it into the
  // pair it always meant: a one-way search, in group mode.
  //
  // Also re-pulls the authoritative wallet balance once rehydration finishes —
  // a plain refresh otherwise rehydrates `loggedIn` + `walletBalance` straight
  // from sessionStorage WITHOUT re-running login(), so the balance shown stays
  // frozen at whatever it was last time and never reflects money added (or
  // spent) since. Best-effort: refreshWallet() is guarded by loggedIn and never
  // throws.
  onRehydrateStorage: () => (state) => {
    if (!state) return;
    if (state.tripType === 'group') {
      state.tripType = 'oneway';
      state.groupBooking = true;
    }
    if (state.flightSearch?.searchedTripType === 'group') {
      state.flightSearch.searchedTripType = 'oneway';
    }
    if (state.loggedIn) { state.refreshWallet?.(); state.refreshContact?.(); }
  },
}));
